/** Shared overlay building blocks. All text comes from t(); components hold no copy. */
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useId, useState } from 'preact/hooks';
import type { Grant } from '../config/content.js';
import { num, t } from '../i18n/index.js';
import { Booster, Coin, Gem, Star } from './icons.js';
import { useBack } from './layer.js';
import { uiSound } from './sound.js';

export function Button(p: {
  variant?: 'primary' | 'secondary' | 'ghost' | 'gem' | 'ad' | 'teal';
  size?: 'lg' | 'md' | 'sm';
  onClick?: () => void;
  disabled?: boolean;
  children: ComponentChildren;
  class?: string;
  label?: string;
  testId?: string;
  pulse?: boolean;
}): JSX.Element {
  const cls = ['btn', `btn-${p.variant ?? 'primary'}`, `btn-${p.size ?? 'md'}`, p.pulse ? 'pulse' : '', p.class ?? ''].join(' ');
  return (
    <button
      type="button"
      class={cls}
      disabled={p.disabled}
      onClick={() => {
        uiSound('button');
        p.onClick?.();
      }}
      aria-label={p.label}
      data-testid={p.testId}
    >
      {p.children}
    </button>
  );
}

export function Modal(p: {
  title?: string;
  children: ComponentChildren;
  class?: string;
  onClose?: () => void;
  hero?: ComponentChildren;
  testId?: string;
}): JSX.Element {
  useBack(p.onClose);
  return (
    <div class="scrim" data-testid={p.testId}>
      <div class={`panel ${p.class ?? ''}`} role="dialog" aria-modal="true" aria-label={p.title}>
        {p.hero && <div class="panel-hero">{p.hero}</div>}
        {p.onClose && (
          <button type="button" class="panel-close" aria-label={t('common.close')} onClick={p.onClose}>
            ×
          </button>
        )}
        {p.title && <h2 class="panel-title">{p.title}</h2>}
        {p.children}
      </div>
    </div>
  );
}

export function Amount({ kind, value, size = 22 }: { kind: 'coins' | 'gems' | 'stars'; value: number | string; size?: number }): JSX.Element {
  return (
    <span class="amount">
      {kind === 'coins' ? <Coin size={size} /> : kind === 'gems' ? <Gem size={size} /> : <Star size={size} />}
      <b>{typeof value === 'number' ? num(value) : value}</b>
    </span>
  );
}

export function GrantView({ grant, size = 26 }: { grant: Grant; size?: number }): JSX.Element {
  return (
    <span class="grant">
      {!!grant.coins && <Amount kind="coins" value={grant.coins} size={size} />}
      {!!grant.gems && <Amount kind="gems" value={grant.gems} size={size} />}
      {Object.entries(grant.boosters ?? {})
        .filter(([, n]) => (n ?? 0) > 0)
        .map(([k, n]) => (
          <span class="amount" key={k}>
            <Booster kind={k} size={size} />
            <b>×{n}</b>
          </span>
        ))}
    </span>
  );
}

/** A switch with its label; `hint` is a line of explanation under it, which the switch is described by. */
export function Toggle(p: { label: string; value: boolean; onChange: (v: boolean) => void; testId?: string; hint?: string }): JSX.Element {
  const hintId = useId();
  return (
    <>
      <label class="toggle-row">
        <span>{p.label}</span>
        <button
          type="button"
          role="switch"
          aria-checked={p.value}
          aria-describedby={p.hint ? hintId : undefined}
          class={`toggle ${p.value ? 'on' : ''}`}
          data-testid={p.testId}
          onClick={() => p.onChange(!p.value)}
        >
          <i />
          <span class="sr">{p.value ? t('common.on') : t('common.off')}</span>
        </button>
      </label>
      {p.hint && (
        <p class="muted small toggle-hint" id={hintId} data-testid={p.testId ? `${p.testId}-hint` : undefined}>
          {p.hint}
        </p>
      )}
    </>
  );
}

/** Renders children only after `ms` (e.g. the win screen's Continue at 1.2 s). */
export function Delayed({ ms, children }: { ms: number; children: ComponentChildren }): JSX.Element | null {
  const [show, setShow] = useState(ms <= 0);
  useEffect(() => {
    if (ms <= 0) return;
    const id = setTimeout(() => setShow(true), ms);
    return () => clearTimeout(id);
  }, [ms]);
  return show ? <>{children}</> : null;
}

/** Counts up to `to` over `ms` for reward reveals. */
export function CountUp({ to, ms = 700 }: { to: number; ms?: number }): JSX.Element {
  const [v, setV] = useState(ms <= 0 ? to : 0);
  useEffect(() => {
    if (ms <= 0) {
      setV(to);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const k = Math.min(1, (now - start) / ms);
      setV(Math.round(to * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, ms]);
  return <>{num(v)}</>;
}
